import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useDebounce from '../../../../hooks/useDebounce';
import { useCategoriesInfiniteQuery } from '../../../../hooks/useCategoriesQuery';
import { CategorySelectView } from './CategorySelectView';
import { Placeholder, createErrorPlaceholder } from '../../../basic';
import { CategoryResponse } from '../../../../types/api/categories';
import { pageSize } from '../../../../services/settings';
import SectionSelector from "./generic/SectionSelector";
import { Text, Stack, TextInput, Button, SkeletonBox } from '@primer/react';
import { SearchIcon, XCircleFillIcon, XCircleIcon, XIcon } from '@primer/octicons-react'

interface CategorySelectProps {
    category: CategoryResponse | undefined;
    didCategorySelected: (category: CategoryResponse) => void;
}

export const CategorySelect: React.FC<CategorySelectProps> = ({ category, didCategorySelected }) => {

    const listRef = useRef<HTMLDivElement | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const {
        isLoading: isLoadingAds,
        data: sectionsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useCategoriesInfiniteQuery(
        debouncedSearchQuery, // search
        pageSize, //limit
        {
            enabled: true
        }
    );

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const handleScroll = () => {
        if (listRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = listRef.current;

            const hasScroll = scrollHeight > clientHeight;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5;

            if (hasScroll && isNearBottom) {
                if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            }
        }
    };

    const title = category?.title ?? " Выберите раздел"
    const emptyListDescription = `По вашему запросу не найдено
    подходящих разделов`;

    return (
        <SectionSelector
            triggerContent={
                <div className="flex flex-col min-w-0">
                    <span className="text-[14px] text-aloix-black font-normal whitespace-nowrap">
                        {title}
                    </span>
                </div>
            }
            containerClassName="flex flex-col h-[444px]"
        >
            {(onClose) => (
                <div>
                    <div>
                        <Text fontSize={14} fontWeight="semibold" className="ml-4 mt-3 block">
                            Выбор раздела
                        </Text>
                        <Stack className="mt-3 h-[360px] block">
                            <TextInput
                                className=' bg-white border border-border rounded-lg ml-4 mr-4 block'
                                leadingVisual={SearchIcon}
                                trailingAction={
                                    searchQuery.length > 0 ? (
                                        <TextInput.Action
                                            onClick={() => {
                                                setSearchQuery('');
                                            }}
                                            icon={XCircleFillIcon}
                                            aria-label="Очистить"
                                        />
                                    ) : undefined
                                }
                                value={searchQuery}
                                placeholder='Поиск'
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                }}
                            />
                            <div className=' border border-border rounded-lg h-[1px]'></div>
                            <div
                                ref={listRef}
                                onScroll={handleScroll}
                                className="w-full flex-1 max-h-screen overflow-y-auto px-4"
                            >
                                {sectionsData?.pages.map((page, i) => (
                                    <React.Fragment key={i}>
                                        {page.list.map((e: CategoryResponse, index) => (
                                            <CategorySelectView
                                                key={e.id}
                                                category={e}
                                                onCategoryClick={() => {
                                                    didCategorySelected(e)
                                                    onClose()
                                                }}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                                <div className="h-4"></div>
                            </div>
                            {!isLoadingAds && !isFetchingNextPage && (sectionsData?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                                <Placeholder
                                    className="mb-4"
                                    icon={<XCircleIcon size={24} className='opacity-40' />}
                                    header='Ничего не было найдено'
                                    description={emptyListDescription}
                                    button={searchQuery.length > 0 && <Button
                                        variant="default"
                                        leadingVisual={XIcon}
                                        onClick={clearSearchInput}>
                                        Очистить</Button>}
                                />
                            )}
                            {(isFetchingNextPage || isLoadingAds) &&
                                <div className="max-h-screen overflow-y-auto">
                                    <SkeletonBox height="4rem" />
                                </div>
                            }
                        </Stack>
                    </div>
                </div>
            )}

        </SectionSelector>
    );
};
